import type { SubscriptionPaymentPeriodUnit } from '~/types'

import type { WorkspaceIdentifier } from '../types'

import { useFetchWithDefaults } from '../useFetchWithDefaults'

export interface PriceListAddonPrice {
  _type: 'addon_price'
  currency_code: string
  id: string
  monthly_price: number
  period: number
  period_unit: string
  price: number
  price_id: string
  pricing_model: string
  title: string
}

export const UKPriceListAddonIds = ['workspace-viewer-pt', 'extra-users-pt', 'unlimited-contracts-pt', 'unlimited-template-access-pt', 'unlimited-e-signing-pt'] as const
export const UserAddonId = 'additional-user-builder-license' as const
export const TemplateAddonId = 'template-license' as const

export interface PriceListAddon {
  _type: 'addon'
  addon_id: (typeof UKPriceListAddonIds)[number] | typeof UserAddonId | (string & {})
  family_id: 'public' | string & {}
  id: string
  prices: PriceListAddonPrice[]
  title: string
  metadata?: Record<string, unknown>
}

export interface PriceListFeatureConfigOption {
  addon_id?: string
  type: 'option'
  quantity: Maybe<number | Record<'month' | 'year', number>>
  description?: string
}

interface PriceListFeatureConfigOptionGroup {
  label: string
  type: 'group'
  options: PriceListFeatureConfigOption[]
}

export interface PriceListFeatureConfig {
  feature_id: string
  options: PriceListFeatureConfigOptionGroup[]
}

export interface PriceListFeature {
  id: string
  title: string
  description?: string
  only_featured?: boolean
}

export interface PriceListFeatureItem {
  id: PriceListFeature['id']
  value: number | string
  description?: string
}

interface PriceListItemEntitlement {
  feature_id: string
  value: number | boolean
}

export interface PriceListItemPrice {
  id: string
  monthly_price: number
  period: number
  period_unit: string
  price: number
}

export interface PriceListItem {
  data: {
    features?: PriceListFeatureItem[] | Record<SubscriptionPaymentPeriodUnit, PriceListFeatureItem[]>
    promoted_features?: PriceListFeatureItem['id'][]
    custom_monthly_price?: Maybe<number | boolean>
    upgrade_only?: boolean
  }
  entitlements: PriceListItemEntitlement[]
  id: string
  position: number
  prices: PriceListItemPrice[]
  title: string
}

export interface PriceList {
  id: string
  _type: 'price_list'
  name: Maybe<string>
  country: App.Internals.Enums.Country
  language: App.Internals.Enums.Language
  state: 'active' | 'draft' | 'hidden'
  data_version: 'v1' | 'v1.1' | 'v1.2' | 'v2'
  data: {
    is_custom_plan_available?: boolean
    feature_configs?: PriceListFeatureConfig[]
    features?: PriceListFeature[]
    promoted_features?: string[]
    info?: string
  }
  addons: PriceListAddon[]
  items: PriceListItem[]
}

export const responseDataTypeName: PriceList['_type'] = 'price_list'

export const useFetchPriceList = () => {
  const { get } = useFetchWithDefaults()
  const url = '/v2/price-lists'

  const getByName = ({
    name,
    workspaceId,
  }: WorkspaceIdentifier & {
    name: string
  }) => get<PriceList>({
    url: `${url}/${name}`,
    workspaceId,
  })

  return {
    getByName,
  }
}
